import React, { useRef, useState } from 'react'
import ObjectiveIcon from './ObjectiveIcon'
import styled from 'particles/themes/styled'
import Text from 'particles/Text'
import { ObjectiveType } from 'state/campaignTemplates'
import { Theme } from 'particles/themes'
import Tooltip from 'particles/Tooltip'

export interface ObjectiveCardProps {
    title: string
    description: string
    type: ObjectiveType
    isSelected?: boolean
    onClick?: () => void
    dataTestId?: string
}

const ObjectiveCard: React.FC<ObjectiveCardProps> = ({
    description,
    type,
    title,
    isSelected = false,
    onClick,
    dataTestId,
}) => {
    const [showTooltip, setShowTooltip] = useState(false)
    const titleRef = useRef<HTMLDivElement | null>(null)
    const descriptionRef = useRef<HTMLDivElement | null>(null)

    // to display tooltip only when title or description are overflow
    const onItemHover = () => {
        const descriptionTotalHeight = descriptionRef.current?.scrollHeight
        const titleTotalWidth = titleRef.current?.scrollWidth
        if (!descriptionTotalHeight && !titleTotalWidth) {
            setShowTooltip(false)
            return
        }

        // since the component is fixed size, then we can set the fix threshold
        const MAX_DESCRIPTION_HEIGHT = 50
        const MAX_TITLE_WIDTH = 120
        const isDescriptionOverflow = (descriptionTotalHeight || 0) > MAX_DESCRIPTION_HEIGHT
        const isTitleOverflow = (titleTotalWidth || 0) > MAX_TITLE_WIDTH

        if (isDescriptionOverflow || isTitleOverflow) {
            setShowTooltip(true)
        } else {
            setShowTooltip(false)
        }
    }

    return (
        // only show the tooltip when `showTooltip` flag is true, otherwise make the tooltip content undefined -> not show
        <Tooltip title={showTooltip ? title : undefined} label={showTooltip ? description : undefined}>
            <ObjectiveCardContainer
                isSelected={isSelected}
                onClick={onClick}
                onMouseEnter={onItemHover}
                data-testid={dataTestId}
            >
                <div className="icon">
                    <ObjectiveIcon type={type} />
                </div>
                <div className="card-content">
                    <Text className="title" scale="bodyM700" color={'primary'} ref={titleRef}>
                        {title}
                    </Text>
                    <Text className="description" scale="caption500" color={'secondary'} ref={descriptionRef}>
                        {description}
                    </Text>
                </div>
            </ObjectiveCardContainer>
        </Tooltip>
    )
}

export default ObjectiveCard

const getBorderColor = ({
    theme,
    isDisabled,
    isSelected,
    defaultValue,
}: {
    theme: Theme
    isDisabled?: boolean
    isSelected?: boolean
    defaultValue: string
}) => {
    if (isDisabled) {
        return theme.colors.border.primary
    }

    if (isSelected) {
        return theme.colors.border.action
    }
    return defaultValue
}

const ObjectiveCardContainer = styled.div<{ isSelected?: boolean; isDisabled?: boolean }>`
    box-sizing: border-box;
    font-family: inherit;
    width: 148px;
    height: 148px;
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.surface.primary};
    border: 2px solid;
    border-color: ${({ theme, isSelected, isDisabled }) =>
        getBorderColor({ theme, isSelected, isDisabled, defaultValue: theme.colors.border.primary })};
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[200]};
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};

    &:hover {
        border-color: ${({ theme, isSelected, isDisabled }) =>
            getBorderColor({ theme, isSelected, isDisabled, defaultValue: theme.colors.border.actionFocus })};
    }

    .card-content {
        flex: 1;
        div {
            cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
            overflow: hidden;
        }

        .title {
            text-overflow: ellipsis;
            width: 100%;
            white-space: nowrap;
        }

        .description {
            -webkit-line-clamp: 3;
            text-overflow: ellipsis;
            -webkit-box-orient: vertical;
            display: -webkit-box;
        }
    }

    .icon {
        display: flex;
        align-items: center;
        gap: 8px;
    }
`
